/**
 * Optional Listening-profile reveals: birthdate (private) → age RANGE, sex, city.
 * Off by default. Birthdate lives only in privateData. Exact age is never
 * published or shown (Sam, Oct 18: age ranges only) — only an opt-in range.
 * City is metro/city only — never street, ZIP, neighborhood, or a map pin.
 */

export const SEX_IDS = ['woman', 'man', 'nonbinary', 'prefer-not', 'self-describe'];

export const SEX_LABELS = {
  woman: 'Woman',
  man: 'Man',
  nonbinary: 'Nonbinary',
  'prefer-not': 'Prefer not to say',
  'self-describe': 'Prefer to self-describe',
};

export const SEX_OPTIONS = SEX_IDS.map(id => ({
  id,
  label: SEX_LABELS[id],
  messageId: `ProfileReveals.sex.${id}`,
}));

// Browse age filter (Sam, Oct 17). Ranges only — exact age / birthdate never searchable.
export const AGE_RANGE_IDS = ['18-29', '30-44', '45-59', '60-plus'];
export const AGE_RANGE_LABELS = {
  '18-29': '18–29',
  '30-44': '30–44',
  '45-59': '45–59',
  '60-plus': '60+',
};
export const AGE_RANGE_OPTIONS = AGE_RANGE_IDS.map(id => ({ id, label: AGE_RANGE_LABELS[id] }));
export const normalizeAgeRange = value => (AGE_RANGE_IDS.includes(value) ? value : '');
export const ageRangeForAge = age => {
  const n = Number(age);
  if (!Number.isInteger(n) || n < 18) return '';
  if (n <= 29) return '18-29';
  if (n <= 44) return '30-44';
  if (n <= 59) return '45-59';
  return '60-plus';
};

export const PROFILE_CITY_MAX_LENGTH = 40;
export const PROFILE_SEX_OTHER_MAX_LENGTH = 40;
export const PROFILE_AGE_MAX_LENGTH = 12;
export const MIN_LISTENER_AGE = 18;
export const MAX_REASONABLE_AGE = 120;

export const BIRTHDATE_ERROR_UNDER_18 = 'under18';
export const BIRTHDATE_ERROR_INVALID = 'invalid';
export const BIRTHDATE_ERROR_FUTURE = 'future';

const STREET_WORD_RE =
  /\b(street|avenue|ave\.|road|rd\.|boulevard|blvd|drive|lane|ln\.|court|suite|apt|apartment|unit|#)\b/i;
const STREET_SUFFIX_ABBREV_RE = /\b(st\.|dr\.|ct\.)(?=\s*$|\s+\d|,)/i;
const ZIP_RE = /\b\d{5}(?:-\d{4})?\b/;
const LEADING_STREET_NO_RE = /^\d+\s/;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_DATE_RE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

export const looksLikeStreetOrZip = value => {
  const text = String(value || '').trim();
  if (!text) {
    return false;
  }
  return (
    ZIP_RE.test(text) ||
    LEADING_STREET_NO_RE.test(text) ||
    STREET_WORD_RE.test(text) ||
    STREET_SUFFIX_ABBREV_RE.test(text)
  );
};

export const normalizeBooleanOptIn = value => value === true || value === 'true';

const pad2 = n => String(n).padStart(2, '0');

const localYmd = (now = new Date()) => ({
  year: now.getFullYear(),
  month: now.getMonth() + 1,
  day: now.getDate(),
});

const isValidCalendarDate = (year, month, day) => {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return false;
  }
  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day
  );
};

/**
 * Parse a birthdate without using Date.parse on YYYY-MM-DD (UTC midnight
 * would shift the calendar day in US timezones).
 *
 * @param {*} value
 * @returns {{ year: number, month: number, day: number }|null}
 */
export const parseBirthdateParts = value => {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }
  const raw = String(value).trim();
  const iso = raw.match(ISO_DATE_RE);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    return isValidCalendarDate(year, month, day) ? { year, month, day } : null;
  }
  const us = raw.match(US_DATE_RE);
  if (us) {
    const month = Number(us[1]);
    const day = Number(us[2]);
    const year = Number(us[3]);
    return isValidCalendarDate(year, month, day) ? { year, month, day } : null;
  }
  return null;
};

export const formatBirthdateISO = parts => {
  if (!parts) {
    return '';
  }
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
};

/**
 * Store as YYYY-MM-DD. Empty if missing or not a real calendar date.
 *
 * @param {*} value
 * @returns {string}
 */
export const normalizeProfileBirthdate = value => {
  const parts = parseBirthdateParts(value);
  return parts ? formatBirthdateISO(parts) : '';
};

/**
 * Whole years from a calendar birthdate. Birthday not yet reached this
 * year (in the local calendar of `now`) → age − 1.
 *
 * @param {string} iso
 * @param {Date} [now]
 * @returns {number|null}
 */
export const ageFromBirthdate = (iso, now = new Date()) => {
  const parts = parseBirthdateParts(iso);
  if (!parts) {
    return null;
  }
  const today = localYmd(now);
  let age = today.year - parts.year;
  if (today.month < parts.month || (today.month === parts.month && today.day < parts.day)) {
    age -= 1;
  }
  return age;
};

export const isFutureBirthdate = (iso, now = new Date()) => {
  const parts = parseBirthdateParts(iso);
  if (!parts) {
    return false;
  }
  const today = localYmd(now);
  if (parts.year > today.year) {
    return true;
  }
  if (parts.year < today.year) {
    return false;
  }
  if (parts.month > today.month) {
    return true;
  }
  if (parts.month < today.month) {
    return false;
  }
  return parts.day > today.day;
};

/**
 * Field-level check. Empty is OK (optional). Codes map to ProfileReveals.* copy.
 *
 * @param {*} value
 * @param {Date} [now]
 * @returns {string|undefined}
 */
export const profileBirthdateError = (value, now = new Date()) => {
  if (value == null || value === '') {
    return undefined;
  }
  const iso = normalizeProfileBirthdate(value);
  if (!iso) {
    return BIRTHDATE_ERROR_INVALID;
  }
  if (isFutureBirthdate(iso, now)) {
    return BIRTHDATE_ERROR_FUTURE;
  }
  const age = ageFromBirthdate(iso, now);
  if (age == null || age > MAX_REASONABLE_AGE) {
    return BIRTHDATE_ERROR_INVALID;
  }
  if (age < MIN_LISTENER_AGE) {
    return BIRTHDATE_ERROR_UNDER_18;
  }
  return undefined;
};

export const todayISODate = (now = new Date()) => formatBirthdateISO(localYmd(now));

export const normalizeProfileAge = value => {
  if (value == null || value === '') {
    return '';
  }
  const raw = String(value).replace(/\s+/g, ' ').trim().slice(0, PROFILE_AGE_MAX_LENGTH);
  if (!raw) {
    return '';
  }
  if (/^\d{1,3}$/.test(raw)) {
    const n = Number(raw);
    return n >= MIN_LISTENER_AGE && n <= 99 ? String(n) : '';
  }
  if (/^\d{2}\s?[-–—]\s?\d{2}$/.test(raw)) {
    const [a, b] = raw.split(/[-–—]/).map(part => Number(part.trim()));
    if (a >= MIN_LISTENER_AGE && b <= 99 && a <= b) {
      return `${a}–${b}`;
    }
    return '';
  }
  if (/^\d{2}s$/i.test(raw)) {
    const n = Number(raw.slice(0, 2));
    return n >= MIN_LISTENER_AGE && n <= 90 ? `${n}s` : '';
  }
  return '';
};

export const normalizeProfileSex = value => (SEX_IDS.includes(value) ? value : '');

export const normalizeProfileSexOther = value => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.replace(/\s+/g, ' ').trim().slice(0, PROFILE_SEX_OTHER_MAX_LENGTH);
};

export const normalizeProfileCity = value => {
  if (typeof value !== 'string') {
    return '';
  }
  const city = value.replace(/\s+/g, ' ').trim().slice(0, PROFILE_CITY_MAX_LENGTH);
  if (!city || looksLikeStreetOrZip(city)) {
    return '';
  }
  return city;
};

export const sexLabelForDisplay = (sex, sexOther) => {
  const id = normalizeProfileSex(sex);
  if (!id || id === 'prefer-not') {
    return '';
  }
  if (id === 'self-describe') {
    return normalizeProfileSexOther(sexOther);
  }
  return SEX_LABELS[id];
};

const publicAgeFromValues = ({ profileBirthdate, profileAge, now }) => {
  if (profileBirthdate) {
    const age = ageFromBirthdate(profileBirthdate, now);
    if (age != null && age >= MIN_LISTENER_AGE && age <= MAX_REASONABLE_AGE) {
      return String(age);
    }
    return '';
  }
  return normalizeProfileAge(profileAge);
};

/**
 * Split form values into private storage + public copies that only exist when opted in.
 * Birthdate is private only. Public age is computed from birthdate, or the
 * leftover numeric age until they add a birthdate.
 *
 * @param {Object} values
 * @param {Date} [now]
 */
export const profileRevealsForSave = (values, now = new Date()) => {
  // Exact age is never published (Sam, Oct 18). showAge is always saved off.
  const showSex = normalizeBooleanOptIn(values?.showSex);
  const showCity = normalizeBooleanOptIn(values?.showCity);
  const showAgeRange = normalizeBooleanOptIn(values?.showAgeRange);
  const birthdateError = profileBirthdateError(values?.profileBirthdate, now);
  const profileBirthdate = birthdateError ? '' : normalizeProfileBirthdate(values?.profileBirthdate);
  const legacyAge = profileBirthdate ? '' : normalizeProfileAge(values?.profileAge);
  const profileSex = normalizeProfileSex(values?.profileSex);
  const profileSexOther =
    profileSex === 'self-describe' ? normalizeProfileSexOther(values?.profileSexOther) : '';
  const profileCity = normalizeProfileCity(values?.profileCity);
  // A private birthdate fills the range; otherwise the listener's own pick.
  // Published only when they opt in (Sam, Oct 17) — default off.
  const birthAge = profileBirthdate ? ageFromBirthdate(profileBirthdate, now) : null;
  const profileAgeRange =
    ageRangeForAge(birthAge) ||
    normalizeAgeRange(values?.profileAgeRange) ||
    (/^\d{1,3}$/.test(legacyAge) ? ageRangeForAge(legacyAge) : '');

  return {
    privateData: {
      profileBirthdate: profileBirthdate || null,
      profileAge: legacyAge || null,
      profileSex: profileSex || null,
      profileSexOther: profileSexOther || null,
      profileCity: profileCity || null,
      profileAgeRange: profileAgeRange || null,
    },
    publicData: {
      showAge: false,
      showAgeRange,
      profileAgeRange: showAgeRange ? profileAgeRange || null : null,
      showSex,
      showCity,
      profileAge: null,
      profileBirthdate: null,
      profileSex: showSex && profileSex && profileSex !== 'prefer-not' ? profileSex : null,
      profileSexOther: showSex && profileSex === 'self-describe' ? profileSexOther || null : null,
      profileCity: showCity ? profileCity || null : null,
    },
  };
};

export const profileRevealsFromListing = listing => {
  const pub = listing?.attributes?.publicData || listing?.publicData || {};
  const priv = listing?.attributes?.privateData || listing?.privateData || {};
  return {
    profileBirthdate: normalizeProfileBirthdate(priv.profileBirthdate),
    profileAge: normalizeProfileAge(priv.profileAge || pub.profileAge),
    profileSex: normalizeProfileSex(priv.profileSex || pub.profileSex),
    profileSexOther: normalizeProfileSexOther(priv.profileSexOther || pub.profileSexOther),
    profileCity: normalizeProfileCity(priv.profileCity || pub.profileCity),
    profileAgeRange: normalizeAgeRange(priv.profileAgeRange || pub.profileAgeRange),
    showAgeRange: normalizeBooleanOptIn(pub.showAgeRange),
    showSex: normalizeBooleanOptIn(pub.showSex),
    showCity: normalizeBooleanOptIn(pub.showCity),
  };
};

/**
 * What strangers see. Never an exact age or birthdate (Sam, Oct 18): only the
 * opt-in age range. Listings saved before Oct 18 with the old “Show age on
 * profile” on show the matching range instead of the number.
 *
 * @param {Object} listing
 * @param {Date} [now]
 */
export const visibleProfileReveals = (listing, now = new Date()) => {
  const pub = listing?.attributes?.publicData || listing?.publicData || {};
  const priv = listing?.attributes?.privateData || listing?.privateData || {};
  const legacyShowAge = normalizeBooleanOptIn(pub.showAge);
  const showSex = normalizeBooleanOptIn(pub.showSex);
  const showCity = normalizeBooleanOptIn(pub.showCity);
  let legacyRange = '';
  if (legacyShowAge) {
    const privateBirthdate = normalizeProfileBirthdate(priv.profileBirthdate);
    const liveAge = privateBirthdate
      ? publicAgeFromValues({ profileBirthdate: privateBirthdate, now })
      : normalizeProfileAge(pub.profileAge);
    legacyRange = /^\d{1,3}$/.test(liveAge) ? ageRangeForAge(liveAge) : '';
  }
  return {
    age: '',
    sex: showSex ? sexLabelForDisplay(pub.profileSex, pub.profileSexOther) : '',
    city: showCity ? normalizeProfileCity(pub.profileCity) : '',
    ageRange: publicAgeRange(listing) || legacyRange,
  };
};

/** Opted-in public age range id ('' when not shared). */
export const publicAgeRange = listing => {
  const pub = listing?.attributes?.publicData || listing?.publicData || {};
  return normalizeBooleanOptIn(pub.showAgeRange) ? normalizeAgeRange(pub.profileAgeRange) : '';
};

/** Opted-in public gender id ('' when not shared). Only showSex listeners count. */
export const publicGender = listing => {
  const pub = listing?.attributes?.publicData || listing?.publicData || {};
  return normalizeBooleanOptIn(pub.showSex) ? normalizeProfileSex(pub.profileSex) : '';
};

// Browse filters (Sam, Oct 17): only listeners who opted in can match.
export const GENDER_PARAM = 'gender';
export const AGE_RANGE_PARAM = 'ageRange';
export const filterListingsByReveals = (listings = [], search = {}) => {
  const gender = search?.[GENDER_PARAM] === 'woman' ? 'woman' : '';
  // Age filter removed (Sam, Oct 21 2026): ?ageRange= in old links is ignored.
  const ranges = [];
  if (!gender && !ranges.length) return listings;
  return listings.filter(
    l =>
      (!gender || publicGender(l) === gender) &&
      (!ranges.length || ranges.includes(publicAgeRange(l)))
  );
};

export const profileRevealSearchHaystack = listing => {
  const visible = visibleProfileReveals(listing);
  return [visible.age, visible.sex, visible.city].filter(Boolean).join(' ');
};
